import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDateTime, formatApiError, openAuthorizedDocument } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, Trash2, Download, Eye, CheckCircle2, AlertCircle, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";

// Collect the union of every required-doc string across all products so we
// can offer a smart dropdown when the user tags an uploaded file.
function useAllRequiredDocs() {
  const [types, setTypes] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => {
      const set = new Set();
      r.data.forEach((p) => (p.documents || []).forEach((d) => set.add(d)));
      setTypes(Array.from(set).sort());
    });
  }, []);
  return types;
}

function UploadCard({ onUploaded }) {
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState(null);
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const types = useAllRequiredDocs();

  const doUpload = async () => {
    if (!file || !type) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", type);
      await api.post("/my-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded: ${file.name}`);
      setFile(null); setType("");
      onUploaded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="qualify-upload-card">
      <h3 className="text-lg font-semibold text-slate-900">Upload a document</h3>
      <p className="mt-1 text-sm text-slate-600">
        Tag each file with its type — we'll match it against every loan program
        so you can see what you qualify for.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
        onClick={() => inputRef.current?.click()}
        data-testid="qualify-drop"
        className={`mt-4 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          drag ? "border-blue-500 bg-blue-50" : file ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"
        }`}
      >
        {file ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <FileText className="h-5 w-5 text-emerald-600" />
            <span className="text-slate-900 font-medium">{file.name}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="ml-2 text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-6 w-6 text-slate-400" />
            <div className="mt-2 text-sm text-slate-700"><span className="font-medium text-blue-600">Click to upload</span> or drag &amp; drop</div>
            <div className="mt-1 text-xs text-slate-500">PDF, Excel, CSV, Word, Images</div>
          </>
        )}
        <input ref={inputRef} type="file" hidden onChange={(e) => setFile(e.target.files?.[0])} data-testid="qualify-file-input" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">Document type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="qualify-type-select" className="bg-white"><SelectValue placeholder="Select what this file is…" /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={doUpload} disabled={busy || !file || !type} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="qualify-upload-btn">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DocLibrary({ docs, onDelete, onPreview }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="qualify-library">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Your document library ({docs.length})</h3>
      </div>
      {docs.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">No documents yet. Upload above to get started.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id} className="px-5 py-3 flex items-center gap-3" data-testid={`qualify-doc-${d.id}`}>
              <FileText className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => onPreview(d)}
                  className="text-sm font-medium text-slate-900 truncate text-left hover:underline underline-offset-4 max-w-full"
                  data-testid={`qualify-doc-name-${d.id}`}
                  title="Preview (scroll through)"
                >
                  {d.filename}
                </button>
                <div className="text-xs text-slate-500 mt-0.5">
                  <span className="text-blue-600">{d.document_type}</span> · {fmtDateTime(d.uploaded_at)}
                </div>
              </div>
              <button
                onClick={() => onPreview(d)}
                className="text-slate-400 hover:text-slate-700"
                data-testid={`qualify-doc-preview-${d.id}`}
                title="Preview inline"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => openAuthorizedDocument(`/my-documents/${d.id}/download`, d.filename).catch((e) => toast.error(e.message || "Could not open document"))}
                className="text-slate-400 hover:text-slate-700"
                data-testid={`qualify-doc-open-${d.id}`}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
              <button onClick={() => onDelete(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`qualify-doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgramCard({ p }) {
  const q = p.qualified;
  return (
    <div className={`rounded-lg border p-5 bg-white transition-all ${q ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`} data-testid={`qualify-program-${p.product.slug}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">{p.product.category}</div>
          <h4 className="mt-1 text-lg font-semibold text-slate-900">{p.product.name}</h4>
          <div className="mt-1 text-xs text-slate-500">${Math.round(p.product.min_amount / 1000)}K – ${(p.product.max_amount / 1_000_000).toFixed(0)}M · {p.product.time_to_close}</div>
        </div>
        {q ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-1 text-xs font-medium">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ready
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-1 text-xs font-medium">
            {p.percent}%
          </span>
        )}
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>{p.have_docs.length} of {p.required_docs.length} documents</span>
          <span>{p.percent}%</span>
        </div>
        <Progress value={p.percent} className="h-1.5" />
      </div>

      {!p.revenue_ok && (
        <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {p.revenue_note}
        </div>
      )}

      {p.missing_docs.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs font-medium text-slate-600 cursor-pointer hover:text-slate-900">
            {p.missing_docs.length} missing {p.missing_docs.length === 1 ? "document" : "documents"} →
          </summary>
          <ul className="mt-2 space-y-1">
            {p.missing_docs.map((d) => (
              <li key={d} className="text-xs text-slate-600 flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-slate-400" /> {d}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
        <Link to={`/programs/${p.product.slug}`} className="text-xs text-slate-500 hover:text-slate-800">View details</Link>
        {q ? (
          <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`qualify-apply-${p.product.slug}`}>
            <Link to="/app/applications/new">Apply now <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        ) : (
          <span className="text-xs text-slate-400">Upload docs to unlock</span>
        )}
      </div>
    </div>
  );
}

export default function Qualify() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = () => {
    api.get("/qualifications").then((r) => setData(r.data));
    api.get("/my-documents").then((r) => setDocs(r.data));
    api.get("/applications").then((r) => setMyApps(r.data || [])).catch(() => setMyApps([]));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Remove this document?")) return;
    try { await api.delete(`/my-documents/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="grid place-items-center h-96 text-sm text-slate-500">Loading…</div>;

  const ready = data.programs.filter((p) => p.qualified);
  const close = data.programs.filter((p) => !p.qualified && p.percent >= 40);
  const rest = data.programs.filter((p) => !p.qualified && p.percent < 40);
  const activeApp = myApps.find((a) => !["Funded", "Declined"].includes(a.stage)) || myApps[0];

  return (
    <div className="max-w-6xl space-y-8" data-testid="qualify-page">
      <div>
        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Auto-qualify</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900">What you qualify for</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Upload your business documents once and we'll instantly show you which
          of our 16 loan programs you're ready to apply for.
        </p>
      </div>

      {activeApp && (
        <div
          className="rounded-lg border-2 border-blue-600 bg-blue-50 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          data-testid="qualify-continue-banner"
        >
          <div>
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Your file is open · {activeApp.loan_number}</div>
            <div className="mt-2 text-lg font-semibold text-slate-900">
              You&apos;re one step away from submitting for funding.
            </div>
            <p className="mt-1 text-sm text-slate-700 max-w-xl">
              Every document you upload attaches to your file. Continue your application to add the
              details we&apos;ll need to approve and fund.
            </p>
          </div>
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="qualify-continue-btn">
            <Link to={`/app/applications/${activeApp.id}`}>Continue application <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UploadCard onUploaded={load} />
        <DocLibrary docs={docs} onDelete={del} onPreview={setPreviewDoc} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-emerald-800">{ready.length}</div>
          <div className="text-xs font-medium text-emerald-700 mt-1">Ready to apply</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-amber-800">{close.length}</div>
          <div className="text-xs font-medium text-amber-700 mt-1">Almost there</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-slate-800">{rest.length}</div>
          <div className="text-xs font-medium text-slate-700 mt-1">Need more docs</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-800">{data.uploaded_types.length}</div>
          <div className="text-xs font-medium text-blue-700 mt-1">Document types</div>
        </div>
      </div>

      {ready.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Ready to apply ({ready.length})
          </h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ready.map((p) => <ProgramCard key={p.product.slug} p={p} />)}
          </div>
        </section>
      )}

      {close.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold text-slate-900">Almost there ({close.length})</h2>
          <p className="text-sm text-slate-600 mt-1">Upload a few more documents to unlock these.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {close.map((p) => <ProgramCard key={p.product.slug} p={p} />)}
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold text-slate-900">Other programs ({rest.length})</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map((p) => <ProgramCard key={p.product.slug} p={p} />)}
          </div>
        </section>
      )}

      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        path={previewDoc ? `/my-documents/${previewDoc.id}/download` : null}
        filename={previewDoc?.filename}
        mimeType={previewDoc?.mime_type}
      />
    </div>
  );
}
